import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { Category, Location } from "@/types";
import { IMG, SITE } from "@/config/site";
import EmergencyPanel from "./EmergencyPanel";

interface HeroProps {
  categories: Category[];
  locations: Location[];
  popularSearches: string[];
  stats: { id: string; value: string; label: string }[];
}

export default function Hero({ categories, locations, popularSearches, stats }: HeroProps) {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState("");
  const [category, setCategory] = useState("");
  const [location, setLocation] = useState("");

  // Keep the badge in sync with the stat cards: pull the "businesses" stat
  // straight from the same data the hero cards render, so the count can never
  // drift apart. Falls back to the original value while data loads.
  const businessStat = stats.find((s) => s.label.toLowerCase().includes("business"));
  const businessCount = businessStat?.value ?? "1,200+";

  const search = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (keyword) params.set("q", keyword);
    if (category) params.set("category", category);
    if (location) params.set("location", location);
    navigate(`/directory?${params.toString()}`);
  };

  return (
    <section className="relative w-full overflow-hidden bg-foreground-950">
      <div className="absolute inset-0">
        <img
          src={IMG.hero}
          alt="Mysore Palace, Mysuru, Karnataka — backdrop of the Mysore Yellow Pages local business directory"
          title="Mysore Palace — Mysore local business directory"
          className="h-full w-full object-cover object-top animate-hero-kenburns"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/45 to-black/78"></div>
      </div>

      <div className="relative mx-auto w-full max-w-[1400px] px-4 pb-12 pt-16 md:px-6 md:pb-16 md:pt-24">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_340px] lg:items-stretch">
          <div className="flex flex-col items-center justify-center text-center">
            <span className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-background-50/30 bg-background-50/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-background-50 backdrop-blur">
              <span className="flex h-3.5 w-3.5 items-center justify-center text-primary-400">
                <i className="ri-verified-badge-fill text-[13px]"></i>
              </span>
              {businessCount} listed businesses across the Mysore region
            </span>
            <h1 className="mt-6 max-w-4xl font-heading text-3xl font-extrabold leading-tight text-background-50 md:text-5xl">
              Discover the best local businesses across {SITE.regions[0]} &amp; beyond
            </h1>
            <p className="mt-5 max-w-2xl text-sm leading-relaxed text-background-100 md:text-base">
              Your trusted directory for {SITE.regions.join(", ")}. Find verified shops, restaurants,
              clinics, hotels and services — with real reviews, working hours and direct contact in one tap.
            </p>

            <div className="mt-8 w-full max-w-3xl">
              <form
                onSubmit={search}
                className="grid grid-cols-1 gap-3 rounded-lg border border-background-200 bg-background-50/95 p-3 backdrop-blur md:grid-cols-[1.6fr_1fr_1fr_auto] md:items-center"
              >
                <div className="relative">
                  <span className="pointer-events-none absolute left-4 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
                    <i className="ri-search-line text-base"></i>
                  </span>
                  <input
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    placeholder="Search business, service or keyword..."
                    className="h-12 w-full rounded-md border border-background-300 bg-background-50 pl-11 pr-4 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                  />
                </div>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  aria-label="Select a category"
                  className="h-12 w-full cursor-pointer appearance-none rounded-md border border-background-300 bg-background-50 px-4 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400"
                >
                  <option value="">All Categories</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                <select
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  aria-label="Select a location"
                  className="h-12 w-full cursor-pointer appearance-none rounded-md border border-background-300 bg-background-50 px-4 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400"
                >
                  <option value="">All Locations</option>
                  {locations.map((l) => (
                    <option key={l.id} value={l.slug}>
                      {l.name}
                    </option>
                  ))}
                </select>
                <button
                  type="submit"
                  className="inline-flex h-12 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 text-sm font-semibold text-foreground-950 transition-colors duration-200 hover:bg-primary-600 cursor-pointer"
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-search-line text-base"></i>
                  </span>
                  Search
                </button>
              </form>
            </div>

            <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
              <span className="text-xs font-medium text-background-200">Popular:</span>
              {popularSearches.slice(0, 6).map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => navigate(`/directory?q=${encodeURIComponent(p)}`)}
                  className="whitespace-nowrap rounded-full border border-background-50/25 bg-background-50/10 px-3 py-1.5 text-xs font-medium text-background-100 backdrop-blur transition-colors hover:border-primary-400 hover:text-primary-300 cursor-pointer"
                >
                  {p}
                </button>
              ))}
            </div>

            <div className="mt-12 grid w-full max-w-3xl grid-cols-2 gap-3 md:grid-cols-4">
              {stats.map((s) => (
                <div
                  key={s.id}
                  className="flex flex-col items-center gap-1 rounded-lg border border-background-50/15 bg-background-50/10 px-4 py-5 backdrop-blur"
                >
                  <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-500 text-foreground-950">
                    <i className="ri-store-3-line text-base"></i>
                  </span>
                  <span className="mt-1 font-heading text-xl font-extrabold text-background-50 md:text-2xl">
                    {s.value}
                  </span>
                  <span className="text-center text-xs text-background-200">{s.label}</span>
                </div>
              ))}
            </div>
          </div>

          <EmergencyPanel />
        </div>
      </div>
    </section>
  );
}